import { useWatch, type FormState } from '@vtex/admin-ui-form'
import { DiscountType, PROMOTION_TYPE } from 'typings'
import {
  BuyAndWinBenefitVariant,
  MoreForLessBenefitVariant,
  RegularBenefitVariant,
} from '../components/typings'

const BuyAndWinDiscountTypes: Record<BuyAndWinBenefitVariant, DiscountType> = {
  gift: DiscountType.Gift,
  freeShipping: DiscountType.Shipping,
  maximumUnitPriceDiscount: DiscountType.Price,
}

const MoreForLessDiscountTypes: Record<
  MoreForLessBenefitVariant,
  DiscountType
> = {
  percentual: DiscountType.Price,
  maxPricePerItem: DiscountType.Price,
}

const RegularDiscountTypes: Record<RegularBenefitVariant, DiscountType> = {
  nominalShipping: DiscountType.Shipping,
  percentualShipping: DiscountType.Shipping,
  freeShipping: DiscountType.Shipping,
  maxShipping: DiscountType.Shipping,
  maxPrice: DiscountType.Price,
  percentual: DiscountType.Price,
  nominal: DiscountType.Price,
  priceTable: DiscountType.Price,
  formula: DiscountType.Formula,
  gift: DiscountType.Gift,
  nominalLoyalty: DiscountType.Loyalty,
  percentualLoyalty: DiscountType.Loyalty,
}

const CONFIGS: Record<
  PROMOTION_TYPE,
  {
    fieldsToObserve: string[]
    getDiscountType: (...args: any[]) => DiscountType | undefined
  }
> = {
  [PROMOTION_TYPE.BUY_TOGETHER]: {
    fieldsToObserve: [],
    getDiscountType: () => DiscountType.Price,
  },
  [PROMOTION_TYPE.BUY_AND_WIN]: {
    fieldsToObserve: ['buyAndWin.benefit.type'],
    getDiscountType: (type: BuyAndWinBenefitVariant | undefined) =>
      type && BuyAndWinDiscountTypes[type],
  },
  [PROMOTION_TYPE.MORE_FOR_LESS]: {
    fieldsToObserve: ['moreForLess.benefit.discount.type'],
    getDiscountType: (type: MoreForLessBenefitVariant) =>
      MoreForLessDiscountTypes[type],
  },
  [PROMOTION_TYPE.PROGRESSIVE]: {
    fieldsToObserve: [],
    getDiscountType: () => DiscountType.Price,
  },
  [PROMOTION_TYPE.REGULAR]: {
    fieldsToObserve: ['regular.benefit.type'],
    getDiscountType: (type: RegularBenefitVariant) =>
      RegularDiscountTypes[type],
  },
  [PROMOTION_TYPE.CAMPAIGN]: {
    fieldsToObserve: ['campaign.benefit.type'],
    getDiscountType: (type: RegularBenefitVariant) =>
      RegularDiscountTypes[type],
  },
}

export function useDiscountType(form: FormState): DiscountType | undefined {
  const promotionType: PROMOTION_TYPE = form.getValues('type')

  const promotionTypeConfig = CONFIGS[promotionType]

  if (!promotionTypeConfig) {
    throw new Error(
      `Cannot determine discount type for promotion type \"${promotionType}\". Every promotion type should have a corresponding configuration in the CONFIGS object.`
    )
  }

  const { fieldsToObserve, getDiscountType } = promotionTypeConfig

  //@ts-expect-error Type 'string[]' is not assignable to type 'string'
  const values = useWatch({ form, name: fieldsToObserve })

  return getDiscountType(...values)
}
